import React, { useState, useEffect } from 'react';
import Cookies from 'js-cookie';
import Dropzone from 'react-dropzone-uploader';
import { FormGroup, Label, Alert, Progress, Badge } from 'reactstrap';
import {
  defaultSubmitState,
  dropzoneDefaultStyleConfig,
  handleCompressImage,
  getBase64,
  addMinutesToDate
} from '../../utils';
import MayarAlert from '../alert/alert';
import { v4 as uuidv4 } from 'uuid';
import { GET_TUS_PRESIGNED_SIGNATURE, GQL_UPDATE_IMAGE, GQL_UPLOAD_IMAGE } from '../../gqls';
import { useMutation, useApolloClient } from '@apollo/client';
import SubmitCoverButton from './submit-cover-button';
import { debounce } from 'lodash';
import * as tus from 'tus-js-client';
import DropzonePreviewComponent from './dropzone-preview-component';
import { multipleCoverContentSetter } from '../../actions/file';
import { useDispatch, useSelector } from 'react-redux';

const MultipleImageVideoInput = ({
  productName,
  setMultipleImageId,
  onLoading,
  label,
  isAutoUploadOnSelectFile = true,
  preserveImageFormat = false,
  setDeletedCoverContents,
  maxVideo = 1,
  maxImage = 5,
  isImageOnly,
  isolatedMultipleCoverContent, // isolated state that used if there is other siblings rendered at the same time.
  setIsolatedMultipleCoverContent // if there is isolated state we dont use redux state
}) => {
  const dispatch = useDispatch();

  const [submitState, setSubmitState] = useState(defaultSubmitState);
  const [loading, setLoading] = useState(false);
  const dropzoneFilesRef = React.useRef([]);
  const [uploadedImages, setUploadedImages] = useState([]); // state at 1 wave uploading, reset every time finished uploading
  const [uploadedVideos, setUploadedVideos] = useState([]); // state at 1 wave uploading, reset every time finished uploading
  const [rejectedVideos, setRejectedVideos] = useState([]); // state at 1 wave uploading, reset every time finished uploading
  const [pickedFiles, setPickedFiles] = useState([]);
  const isUploadingRef = React.useRef(false);
  const resetSubmitState = () => setSubmitState(defaultSubmitState);

  const multipleCoverContentReduxState = useSelector(state => state.multipleCoverContent);
  const multipleCoverContentItems = setIsolatedMultipleCoverContent
    ? isolatedMultipleCoverContent
    : multipleCoverContentReduxState;

  const [uploadImage] = useMutation(GQL_UPLOAD_IMAGE);
  const [editImage] = useMutation(GQL_UPDATE_IMAGE);
  const client = useApolloClient();

  useEffect(() => {
    onLoading(loading);
  }, [loading]);

  useEffect(() => {
    if (!loading) return;
    if (rejectedVideos?.length) {
      setUploadedImages([]);
      setRejectedVideos([]);
      setUploadedVideos([]);
      setLoading(false);
      setSubmitState({
        isError: true,
        isSuccess: false,
        message: 'Upload gagal, silahkan coba lagi'
      });
    } else if (pickedFiles.length && pickedFiles.length === uploadedVideos.length + uploadedImages.length) {
      if (setIsolatedMultipleCoverContent) {
        setIsolatedMultipleCoverContent(prev => {
          const existingServerContent = prev.filter(c => !c.isLocal);
          return [
            ...existingServerContent,
            ...uploadedVideos.map((item, index) => ({
              ...item,
              position: `${existingServerContent.length + index}`
            }))
          ];
        });
      } else {
        const existingServerContent = (multipleCoverContentItems || []).filter(c => !c.isLocal);
        dispatch(
          multipleCoverContentSetter([
            ...existingServerContent,
            ...uploadedVideos.map((item, index) => ({
              ...item,
              position: `${existingServerContent.length + index}`
            }))
          ])
        );
      }
      setRejectedVideos([]);
      setUploadedVideos([]);
      setUploadedImages([]);
      setLoading(false);
      setSubmitState({
        isError: false,
        isSuccess: true,
        message: 'Semua cover berhasil diupload!'
      });
      if (!isAutoUploadOnSelectFile) {
        isUploadingRef.current = true;
        pickedFiles.forEach(f => f.remove());
        isUploadingRef.current = false;
      }
    }
  }, [uploadedImages, uploadedVideos, rejectedVideos, pickedFiles]);

  const uploadImageIteration = async (e, imageId) => {
    const values = {};
    const fileType = preserveImageFormat ? e.file.type : 'image/jpeg';
    const compressedImage = await handleCompressImage(e.file, null, fileType);
    const base64 = await getBase64(compressedImage);

    values.base64 = base64;
    values.fileType = compressedImage.type.split('/')[1];
    values.title = imageId;

    const res = await uploadImage({
      variables: values
    });

    return res;
  };

  const handleTusResumableUpload = async (file = {}, signature = {}, videoId) => {
    try {
      if (!signature.signature || !signature.expirationTime) {
        setSubmitState({
          isError: true,
          isSuccess: false,
          message: 'Upload gagal, silahkan coba lagi'
        });
        return;
      }

      const upload = new tus.Upload(file, {
        endpoint: (process.env.REACT_APP_MAYAR_TUS_URL || 'http://localhost:1080') + '/upload',
        retryDelays: [0, 3000, 5000, 10000, 20000, 60000, 60000],
        headers: {
          Authorization: 'Bearer ' + Cookies.get('token'),
          AuthorizationSignature: signature.signature,
          AuthorizationExpire: signature.expirationTime,
          VideoId: videoId
        },
        metadata: {
          filetype: file.type,
          title: file.name,
          videoId
        },
        onError: err => {
          setRejectedVideos(prev => {
            return [
              ...prev,
              {
                file,
                error: err || 'form.uploadFailed',
                videoId
              }
            ];
          });
        },
        onProgress: () => { },
        onSuccess: () => {
          setUploadedVideos(prevState => {
            return [
              ...(prevState || []),
              {
                file,
                videoId: upload[videoId + '-previous-upload']?.metadata?.videoId || videoId
              }
            ];
          });
        }
      });

      upload.findPreviousUploads().then(function (previousUploads) {
        if (previousUploads.length) {
          upload[videoId + '-previous-upload'] = previousUploads[0]; // store the previous upload video id to replace new created video id
          upload.resumeFromPreviousUpload(previousUploads[0]);
        }

        upload.start();
      });
    } catch (err) {
      setSubmitState({
        isError: true,
        isSuccess: false,
        message: 'Upload gagal, silahkan coba lagi'
      });
    }
  };

  const uploadVideoIteration = async e => {
    const videoId = uuidv4();

    const { data } = await client.query({
      query: GET_TUS_PRESIGNED_SIGNATURE,
      variables: {
        videoId,
        isLocalTusServer: true
      },
      fetchPolicy: 'no-cache'
    });

    const signatureResult = data?.getTusPresignedSignature;

    await handleTusResumableUpload(e.file, signatureResult, videoId);
    return null;
  };

  const incrementUpdatedAtImage = async (file, indexItem) => {
    const minutes = (indexItem + 1) * 10;
    const newUpdatedAt = addMinutesToDate(file.data.uploadImage.updatedAt, minutes);

    const res = await editImage({
      variables: {
        input: {
          id: file.data.uploadImage.id,
          updatedAt: newUpdatedAt
        }
      }
    });

    return await res?.data;
  };

  const handleCoverUpload = async files => {
    isUploadingRef.current = true;
    setLoading(true);
    setPickedFiles(files);
    setSubmitState(defaultSubmitState);

    // separate files by type images and videos
    // condition if files type images
    const imageTitleId = uuidv4();

    let images = [];
    let videos = [];

    if (!isAutoUploadOnSelectFile) {
      // Use files from multipleCoverContentItems to include cropped versions
      const localContent = multipleCoverContentItems.filter(c => c.isLocal);
      images = localContent.filter(c => c.file.type.includes('image'));
      videos = localContent.filter(c => c.file.type.includes('video'));
    } else {
      images = files.filter(f => f.file.type.includes('image'));
      videos = files.filter(f => f.file.type.includes('video'));
    }

    const totalVideosCount = [];
    const totalImagesCount = [];
    const serverFiles = (multipleCoverContentItems || []).filter(item => !item.isLocal);
    const combinedFiles = [...serverFiles, ...images, ...videos];

    for (const item of combinedFiles) {
      if ((item.file?.mimetype || item.file?.type || item.type || '').includes('video') || item?.videoId || item?.value) totalVideosCount.push(item);
      else totalImagesCount.push(item);
    }

    if (totalVideosCount.length > maxVideo) {
      setSubmitState({
        isError: true,
        isSuccess: false,
        message: `Telah mencapai batas maksimal jumlah video (${maxVideo})`
      });
      setLoading(false);
      isUploadingRef.current = false;
      return;
    }
    if (totalImagesCount.length > maxImage) {
      setSubmitState({
        isError: true,
        isSuccess: false,
        message: `Telah mencapai batas maksimal jumlah gambar (${maxImage})`
      });
      setLoading(false);
      isUploadingRef.current = false;
      return;
    }

    const [imageResults] = await Promise.all([
      Promise.all(images.map(i => uploadImageIteration(i, imageTitleId))),
      Promise.all(videos.map(i => uploadVideoIteration(i)))
    ]);

    const updatedImageArray = await Promise.all(
      imageResults.map(async (i, indexRes) => incrementUpdatedAtImage(i, indexRes))
    );

    if (updatedImageArray.length && updatedImageArray.length === images.length) {
      if (setMultipleImageId) {
        setMultipleImageId(imageTitleId);
      }

      if (setIsolatedMultipleCoverContent) {
        setIsolatedMultipleCoverContent(prev => {
          const existingServerContent = prev.filter(c => !c.isLocal);
          return [
            ...existingServerContent,
            ...updatedImageArray.map((item, index) => ({
              ...item,
              position: `${existingServerContent.length + index}`
            }))
          ];
        });
      } else {
        const existingServerContent = (multipleCoverContentItems || []).filter(c => !c.isLocal);
        dispatch(
          multipleCoverContentSetter([
            ...existingServerContent,
            ...updatedImageArray.map((item, index) => ({
              ...item,
              position: `${existingServerContent.length + index}`
            }))
          ])
        );
      }

      setUploadedImages(updatedImageArray);

      if (!videos?.length) {
        setSubmitState({
          isError: false,
          isSuccess: true,
          message: 'Semua cover berhasil diupload!'
        });
        if (!isAutoUploadOnSelectFile) {
          files.forEach(f => f.remove());
        }
      }
    } else if (!videos?.length) {
      setSubmitState({
        isError: true,
        isSuccess: false,
        message: 'Upload gagal, silahkan coba lagi'
      });
    }

    if (!videos?.length) {
      setLoading(false);
    }
    isUploadingRef.current = false;
  };

  const handleCroppedImageUpload = async (blob, index) => {
    if (!isAutoUploadOnSelectFile) {
      // Local mode: update the local file in multipleCoverContentItems
      const newContent = [...multipleCoverContentItems];
      const currentItem = newContent[index];
      const newFile = new window.File([blob], currentItem.file?.name || 'cropped.jpeg', { type: 'image/jpeg' });

      newContent[index] = { ...currentItem, file: newFile };

      if (setIsolatedMultipleCoverContent) {
        setIsolatedMultipleCoverContent(newContent);
      } else {
        dispatch(multipleCoverContentSetter(newContent));
      }

      setSubmitState({
        isError: false,
        isSuccess: true,
        message: 'Gambar berhasil dipotong secara lokal!'
      });
      return;
    }

    setLoading(true);
    try {
      const imageTitleId = uuidv4();
      const file = new window.File([blob], 'cropped.jpeg', { type: 'image/jpeg' });

      const res = await uploadImageIteration({ file }, imageTitleId);
      const updatedImage = await incrementUpdatedAtImage(res, 0);

      const newContent = [...multipleCoverContentItems];
      newContent[index] = { ...updatedImage, position: `${index}` };

      if (setIsolatedMultipleCoverContent) {
        setIsolatedMultipleCoverContent(newContent);
      } else {
        dispatch(multipleCoverContentSetter(newContent));
      }

      setSubmitState({
        isError: false,
        isSuccess: true,
        message: 'Gambar berhasil dipotong!'
      });
    } catch (err) {
      setSubmitState({
        isError: true,
        isSuccess: false,
        message: 'Gagal mengupdate gambar setelah dipotong'
      });
    }
    setLoading(false);
  };

  const debouncedHandleCoverUpload = debounce(handleCoverUpload, 500);

  return (
    <FormGroup>
      {label || <Label for="cover">Cover (gambar/video untuk promo)</Label>}
      {!isAutoUploadOnSelectFile && (
        <>
          <Alert color="info" className="font-12 py-5 px-10 mb-10">
            <strong>Info Pengunggahan:</strong> Gambar yang Anda pilih saat ini disimpan secara lokal di browser Anda dan belum diunggah. Klik tombol <strong>&apos;Unggah Gambar&apos;</strong> untuk menyimpannya ke server.
          </Alert>
          <Alert color="warning" className="font-12 py-5 px-10 mb-10">
            <strong>Info Pemotongan (Crop):</strong> Anda hanya dapat memotong (crop) gambar yang baru saja dipilih (bertanda <Badge color="success">New</Badge>). Gambar yang sudah tersimpan sebelumnya tidak dapat dipotong ulang di sini.
          </Alert>
        </>
      )}

      <Alert color="secondary" className="font-12 py-5 px-10 mb-10">
        {isImageOnly ? (
          <><strong>Batas Maksimal:</strong> Anda dapat mengunggah hingga <strong>{maxImage} gambar</strong>.</>
        ) : (
          <><strong>Batas Maksimal:</strong> Anda dapat mengunggah hingga <strong>{maxImage} gambar</strong> dan <strong>{maxVideo} video</strong>.</>
        )}
      </Alert>

      <MayarAlert
        isError={submitState.isError}
        isSuccess={submitState.isSuccess}
        message={submitState.message}
        toggle={resetSubmitState}
        className="mb-10"
      />

      {loading && (
        <Alert color="light">
          <Progress animated color="primary" value="100" />
          Sedang mengunggah...
        </Alert>
      )}

      {!loading && (
        <>
          <Dropzone
            onSubmit={handleCoverUpload}
            name="dropzone"
            accept={isImageOnly ? 'image/*' : 'image/*,video/*'}
            maxFiles={5}
            maxSizeBytes={524288000}
            canRemove={!loading}
            PreviewComponent={() => {
              return <></>;
            }}
            SubmitButtonComponent={props =>
              isAutoUploadOnSelectFile ? null : <SubmitCoverButton props={props} loading={loading} />
            }
            onChangeStatus={(...args) => {
              const files = args[2];
              dropzoneFilesRef.current = files;

              const totalFiles = [
                ...(multipleCoverContentItems || []).filter(item => !item.isLocal),
                ...files.filter(f => f.meta.status !== 'removed')
              ];
              const totalVideos = [];
              const totalImages = [];

              for (const item of totalFiles) {
                if ((item.file?.mimetype || item.file?.type || '').includes('video') || item?.videoId || item?.value)
                  totalVideos.push(item);
                else totalImages.push(item);
              }

              if (totalVideos.length > maxVideo) {
                setSubmitState({
                  isError: true,
                  isSuccess: false,
                  message: `Telah mencapai batas maksimal jumlah video (${maxVideo})`
                });

                // Remove the offending file from dropzone immediately
                const lastFile = args[0];
                if (lastFile?.remove) lastFile.remove();
                return; // bail out — don't update state
              }

              if (totalImages.length > maxImage) {
                setSubmitState({
                  isError: true,
                  isSuccess: false,
                  message: `Telah mencapai batas maksimal jumlah gambar (${maxImage})`
                });
                const lastFile = args[0];
                if (lastFile?.remove) lastFile.remove();
                return; // bail out — don't update state
              }

              if (!isAutoUploadOnSelectFile && !loading && !isUploadingRef.current) {
                // 1. Start with existing content but filter out local files that are no longer in Dropzone
                const currentContent = (multipleCoverContentItems || []).filter(c => !c.isLocal || files.some(f => f.meta.id === c.id));

                // 2. Add new files from Dropzone that aren't in multipleCoverContent yet
                const newFilesFromDropzone = files
                  .filter(f => f.meta.status === 'done' && !currentContent.some(c => c.id === f.meta.id))
                  .map((f, i) => ({
                    file: f.file,
                    position: `${currentContent.length + i}`,
                    isLocal: true,
                    isNew: true,
                    id: f.meta.id
                  }));

                const newContent = [...currentContent, ...newFilesFromDropzone];

                // Only update if something actually changed to avoid infinite loops
                if (JSON.stringify(newContent.map(c => c.id)) !== JSON.stringify((multipleCoverContentItems || []).map(c => c.id))) {
                  if (setIsolatedMultipleCoverContent) {
                    setIsolatedMultipleCoverContent(newContent);
                  } else {
                    dispatch(multipleCoverContentSetter(newContent));
                  }
                }
              }

              if (
                isAutoUploadOnSelectFile &&
                !loading &&
                args[1] === 'done' &&
                !files.every(el => el.file.name.includes(productName))
              ) {
                debouncedHandleCoverUpload(files);
              }
            }}
            inputContent={(files, extra) => (
              <div className="d-flex flex-column align-items-center justify-content-center">
                {extra.reject ? 'Image files only' : 'Drag & drop image'}
              </div>
            )}
            styles={{
              ...dropzoneDefaultStyleConfig.styles,
              inputLabel: {
                ...dropzoneDefaultStyleConfig.styles.inputLabel,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100%',
                margin: 0,
                width: '100%'
              },
              inputLabelWithFiles: {
                ...dropzoneDefaultStyleConfig.styles.inputLabelWithFiles,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                width: 'auto',
                margin: '10px auto',
              }
            }}
            classNames={dropzoneDefaultStyleConfig.classNames}
          />
          {!!multipleCoverContentItems?.length && (
            <>
              <Alert color="light" className="mt-10 mb-10 text-center">
                Tekan dan geser untuk merubah urutan konten pada cover.
              </Alert>
              <DropzonePreviewComponent
                multipleCoverContent={multipleCoverContentItems}
                setMultipleCoverContent={
                  setIsolatedMultipleCoverContent
                    ? setIsolatedMultipleCoverContent
                    : val => dispatch(multipleCoverContentSetter(val))
                }
                setDeletedCoverContents={setDeletedCoverContents}
                handleCroppedImageUpload={handleCroppedImageUpload}
                onRemoveLocalFile={id => {
                  const fileToRemove = dropzoneFilesRef.current.find(f => f.meta.id === id);
                  if (fileToRemove) {
                    fileToRemove.remove();
                  }
                }}
              />
            </>
          )}
        </>
      )}
    </FormGroup>
  );
};

MultipleImageVideoInput.defaultProps = {
  onLoading: () => { }
};

export default MultipleImageVideoInput;
